import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import axios from "axios";
import {
  API_BASE,
  setSessionExpiredHandler,
  setActiveOrganizationId,
  fetchCurrentOrganization,
  selectOrganization,
} from "./api.js";
import { getPendingInviteToken } from "./inviteFlow.js";

const ACTIVE_ORG_STORAGE_KEY = "activeOrganizationId";

const normalizeEmail = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : email;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [activeOrganization, setActiveOrganization] = useState(null);

  const applyActiveOrganization = useCallback((active) => {
    setActiveOrganization(active || null);
    if (active?.organizationId) {
      setActiveOrganizationId(active.organizationId);
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, active.organizationId);
    } else {
      setActiveOrganizationId(null);
    }
  }, []);

  const syncUserOrganizationRole = useCallback((role) => {
    if (!role) return;
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, organizationRole: role };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const refreshOrganizationContext = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      setOrganizations([]);
      setActiveOrganization(null);
      setActiveOrganizationId(null);
      return null;
    }

    const data = await fetchCurrentOrganization();
    const orgs = data.organizations || [];
    setOrganizations(orgs);

    let active = data.activeOrganization || null;
    const preferredId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    const preferredIsValid =
      preferredId && orgs.some((org) => org.organizationId === preferredId);

    const pendingInviteToken = getPendingInviteToken();
    if (
      !pendingInviteToken &&
      preferredIsValid &&
      preferredId !== active?.organizationId
    ) {
      const selected = await selectOrganization(preferredId);
      active = selected.activeOrganization || active;
    }

    applyActiveOrganization(active);
    if (active?.role) {
      syncUserOrganizationRole(active.role);
    }
    return active;
  }, [applyActiveOrganization, syncUserOrganizationRole]);

  const selectWorkspace = useCallback(
    async (organizationId) => {
      const result = await selectOrganization(organizationId);
      applyActiveOrganization(result.activeOrganization);
      if (result.activeOrganization?.role) {
        syncUserOrganizationRole(result.activeOrganization.role);
      }
      return result.activeOrganization;
    },
    [applyActiveOrganization, syncUserOrganizationRole]
  );

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      localStorage.removeItem("token");
      delete axios.defaults.headers.common["Authorization"];
      setActiveOrganizationId(null);
    }
  }, [token]);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        const savedUser = localStorage.getItem("user");
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
        await refreshOrganizationContext();
      } catch (err) {
        console.error("Token validation error:", err);
        setToken(null);
        setUser(null);
        setOrganizations([]);
        setActiveOrganization(null);
        setActiveOrganizationId(null);
      }
      setLoading(false);
    };

    validateToken();
  }, [token, refreshOrganizationContext]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setSessionExpired(true);
      setToken(null);
      setUser(null);
      setOrganizations([]);
      setActiveOrganization(null);
      setActiveOrganizationId(null);
      delete axios.defaults.headers.common["Authorization"];
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = async (email, password) => {
    try {
      setSessionExpired(false);
      const normalizedEmail = normalizeEmail(email);
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email: normalizedEmail,
        password,
      });

      setToken(response.data.token);
      setUser(response.data.user);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      await refreshOrganizationContext();
      return response.data;
    } catch (error) {
      console.error("Login error:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
      throw error;
    }
  };

  const register = async (email, password, dealCode = null) => {
    try {
      setSessionExpired(false);
      const normalizedEmail = normalizeEmail(email);
      const response = await axios.post(
        `${API_BASE}/auth/register`,
        {
          email: normalizedEmail,
          password,
          dealCode,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.token || !response.data.user) {
        throw new Error("Invalid response format from server");
      }

      setToken(response.data.token);
      setUser(response.data.user);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      await refreshOrganizationContext();
      return response.data;
    } catch (error) {
      console.error("Registration error details:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        stack: error.stack,
      });
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setOrganizations([]);
    setActiveOrganization(null);
    setActiveOrganizationId(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    sessionExpired,
    organizations,
    activeOrganization,
    refreshOrganizationContext,
    selectWorkspace,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
